import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/shared/components/ui/sheet";
import { Button } from "@/shared/components/ui/button";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus,
  MessageCircle,
  MoreVertical,
  Edit2,
  Archive,
  Trash2,
  Clock,
  MessageSquare,
} from "lucide-react";
import { cn } from "@/infrastructure/lib/utils";
import { ChatBase } from "../types/chatBase";
import { useChatBaseContext } from "../context/ChatBaseContext";
import { BackgroundTasksMarker } from "./BackgroundTasksIndicator";

interface ChatListProps {
  isOpen: boolean;
  onClose: () => void;
  /** Tarefas em segundo plano rodando em cada conversa (AB#86889). */
  backgroundCountFor?: (chat: ChatBase) => number;
}

export default function ChatList({ isOpen, onClose, backgroundCountFor }: ChatListProps) {
  const { t, i18n } = useTranslation();
  const {
    chats,
    selectedChatId,
    isLoadingChats,
    selectChat,
    renameChat,
    archiveChat,
    deleteChat,
  } = useChatBaseContext();

  // Estado para renomeação inline
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);

  // Estado para confirmação de exclusão
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);

  /**
   * Iniciar edição de título
   */
  const startEditing = (chat: ChatBase) => {
    setEditingChatId(chat.id);
    setEditingTitle(chat.title);
  };

  /**
   * Cancelar edição
   */
  const cancelEditing = () => {
    setEditingChatId(null);
    setEditingTitle("");
  };

  /**
   * Salvar edição de título
   */
  const saveEditing = async () => {
    if (!editingChatId || !editingTitle.trim()) {
      cancelEditing();
      return;
    }

    const success = await renameChat(editingChatId, editingTitle.trim());
    if (success) {
      cancelEditing();
    }
  };

  /**
   * Selecionar chat e fechar sidebar
   */
  const handleSelectChat = (chatId: string) => {
    selectChat(chatId);
    onClose();
  };

  /**
   * Preparar para nova conversa (não cria chat ainda)
   */
  const handlePrepareNewChat = () => {
    selectChat(null); // Limpar seleção atual
    onClose(); // Fechar sidebar
  };

  /**
   * Confirmar exclusão de chat
   */
  const handleDeleteChat = async () => {
    if (!deletingChatId) return;
    
    const success = await deleteChat(deletingChatId);
    if (success) {
      setDeletingChatId(null);
    }
  };

  /**
   * Formatar timestamp relativo
   */
  const formatRelativeTime = useCallback((dateString: string | null) => {
    if (!dateString) return null;
    
    try {
      const locale = i18n.language === 'pt' ? ptBR : enUS;
      
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale,
      });
    } catch {
      return null;
    }
  }, [i18n.language]);

  // Focar no input quando começar a editar
  useEffect(() => {
    if (editingChatId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingChatId]);

  return (
    <>
      {/* Flyout/Sheet principal */}
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="w-[400px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              {t('playground.chatList.title')}
            </SheetTitle>
            <SheetDescription>
              {t('playground.chatList.description')}
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-col h-full mt-6">
            {/* Botão Criar Novo Chat */}
            <div className="mb-4">
              <Button 
                onClick={handlePrepareNewChat}
                className="w-full flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                {t('playground.chatList.newChat')}
              </Button>
            </div>

            {/* Lista de Chats */}
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-2">
                {isLoadingChats ? (
                  // Loading state
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="p-3 border border-border rounded-lg bg-muted/50 animate-pulse"
                      >
                        <div className="h-4 bg-muted-foreground/20 rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-muted-foreground/20 rounded w-1/2"></div>
                      </div>
                    ))}
                  </div>
                ) : chats.length === 0 ? (
                  // Empty state
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" />
                    <p className="text-sm text-muted-foreground mb-2">
                      {t('playground.chatList.noChatsYet')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('playground.chatList.createFirstChat')}
                    </p>
                  </div>
                ) : (
                  // Lista de chats
                  chats.map((chat) => (
                    <div
                      key={chat.id}
                      className={cn(
                        "group relative p-3 border border-border rounded-lg cursor-pointer transition-all hover:bg-muted/50",
                        selectedChatId === chat.id && "bg-muted border-primary/50 ring-1 ring-primary/20"
                      )}
                      onClick={() => editingChatId !== chat.id && handleSelectChat(chat.id)}
                    >
                      {/* Título do Chat */}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        {editingChatId === chat.id ? (
                          // Modo de edição
                          <Input
                            ref={editInputRef}
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onBlur={saveEditing}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEditing();
                              if (e.key === 'Escape') cancelEditing();
                            }}
                            className="h-7 text-sm font-medium"
                            maxLength={255}
                          />
                        ) : (
                          // Modo de visualização
                          <h3 
                            className="text-sm font-medium text-foreground line-clamp-2 flex-1 pr-2"
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              startEditing(chat);
                            }}
                          >
                            {chat.title}
                          </h3>
                        )}
                        {editingChatId !== chat.id && (
                          <BackgroundTasksMarker count={backgroundCountFor?.(chat) ?? chat.background_tasks_count ?? 0} />
                        )}

                        {/* Menu de ações */}
                        {editingChatId !== chat.id && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreVertical className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => startEditing(chat)}>
                                <Edit2 className="h-4 w-4 mr-2" />
                                {t('playground.chatList.renameChat')}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => archiveChat(chat.id)}
                                className="text-orange-600 hover:text-orange-700"
                              >
                                <Archive className="h-4 w-4 mr-2" />
                                {t('playground.chatList.archiveChat')}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => setDeletingChatId(chat.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                {t('playground.chatList.deleteChat')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>

                      {/* Metadados do Chat */}
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-3">
                          {/* Contagem de mensagens */}
                          {chat.message_count > 0 && (
                            <Badge variant="secondary" className="px-1.5 py-0 text-xs">
                              {chat.message_count} {chat.message_count === 1 ? t('playground.chatList.messageCount.singular') : t('playground.chatList.messageCount.plural')}
                            </Badge>
                          )}
                        </div>

                        {/* Última atividade */}
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <span>
                            {formatRelativeTime(chat.last_message_at || chat.updated_at) || t('playground.chatList.timeAgo')}
                          </span>
                        </div>
                      </div>

                      {/* Indicador de chat selecionado */}
                      {selectedChatId === chat.id && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r"></div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>

            {/* Rodapé com informações */}
            {chats.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground text-center">
                {chats.length} {chats.length === 1 ? t('playground.chatList.activeChats.singular') : t('playground.chatList.activeChats.plural')}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Dialog de confirmação para exclusão */}
      <AlertDialog open={!!deletingChatId} onOpenChange={() => setDeletingChatId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('playground.chatList.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('playground.chatList.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteChat}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('playground.chatList.deleteChat')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
